<template>
    <app-layout>
        <base-header :shadow="false" border border-bg-from="from-indigo-500" border-bg-to="to-purple-500">
            <div class="flex items-center justify-between w-full">
                <div>
                    <h4 class="text-xl font-medium">
                        {{ $t('general.imports') }}
                    </h4>
                </div>

                <div class="flex items-center">
                    <default-button icon="plus" size="sm" @click.native="form.showModal = true">
                        {{ $t('import.create') }}
                    </default-button>
                </div>
            </div>
        </base-header>

        <table-base class="text-sm" :busy="isLoading">
            <template #head>
                <table-head sort="date" :sort-query="sortQuery">{{ $t('date.date') }}</table-head>
                <table-head>{{ $t('general.type') }}</table-head>
                <table-head>{{ $t('general.status') }}</table-head>
                <table-head>{{ $t('import.imported_at') }}</table-head>
                <table-head>{{ $t('general.error') }}</table-head>
                <table-head></table-head>
            </template>
            <template #body>
                <table-row v-for="importEntry in imports.data" :key="importEntry.id">
                    <table-cell>
                        {{ importEntry.created_at | moment('DD. MMMM YYYY HH:mm:ss') }}
                    </table-cell>
                    <table-cell>
                        <span class="px-2 py-1 rounded-lg uppercase bg-blue-600 font-bold text-xs">
                            {{ importEntry.type }}
                        </span>
                    </table-cell>
                    <table-cell>
                        <span class="px-2 py-1 rounded-lg uppercase font-bold text-xs"
                              :class="getBadgeStylesFromStatus(importEntry.status)">
                            {{ importEntry.status }}
                        </span>
                    </table-cell>
                    <table-cell>
                        <template v-if="importEntry.imported_at">
                            {{ importEntry.imported_at | moment('DD. MMMM YYYY HH:mm:ss') }}
                        </template>
                    </table-cell>
                    <table-cell>
                        <template v-if="importEntry.error">
                            <span class="text-red-400 flex">
                                <icon name="exclamation"></icon>
                                &nbsp;
                                {{ importEntry.error }}
                            </span>
                        </template>
                    </table-cell>
                    <table-cell class="flex justify-end space-x-1">
                        <default-button
                            icon="download"
                            :label="$t('general.download')"
                            :is-inertia="false"
                            :href="importEntry.url"
                            should-target-blank
                        />
                        <default-button
                            v-if="importEntry.status === 'preview'"
                            icon="check"
                            :label="$t('import.import_verb')"
                            :loading="isFetchingChangelist.includes(importEntry.id)"
                            @click.native="confirmImport(importEntry)"
                        />
                    </table-cell>
                </table-row>
            </template>
        </table-base>
        <table-pagination v-if="imports && imports.meta" :meta="imports.meta.pagination" :links="imports.links"
                          @paginate="loadPage"/>

        <element-modal :title="$t('general.import')" :open="form.showModal" @close="resetForm">
            <template #body>
                <span class="flex flex-col space-y-2">
                    <multiselect v-model="form.type" :options="importTypeOptions" label="label" track-by="value"></multiselect>
                    <input type="file" @change="form.file = $event.target.files[0]">
                </span>
            </template>

            <template #footer>
                <default-button class="ml-2" @click.native="previewImport" :disabled="!form.file || !form.type"
                                :loading="form.isLoading">
                    {{ $t('general.preview') }}
                </default-button>
            </template>
        </element-modal>

        <element-modal :title="$t('general.import')" :open="confirm.showModal" @close="resetConfirm"
                       width="sw-full md:w-3/4 lg:w-1/2 xl:w-1/2"
        >
            <template #body>
                <data-table :auth="auth" :data-type="confirm.dataType" :collection="confirm.collection"
                            :has-comparison-columns="false" :is-import-modal="true"
                ></data-table>
            </template>

            <template #footer>
                <default-button class="ml-2" @click.native="startImport" :loading="confirm.isLoading">
                    {{ $t('import.import_verb') }}
                </default-button>
            </template>
        </element-modal>
    </app-layout>
</template>

<script>
import Import from '@/Models/Import';
import DataTable from '@/Components/Storemanager/DataTable';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import Actual from '@/Models/Actual';
import i18n from '@/Plugins/i18n';
import Store from '@/Models/Store';

export default {
    components: {DataTable},

    data: function() {
        return {
            imports: {},
            isDeleting: [],
            isFetchingChangelist: [],
            isLoading: true,
            page: 1,
            sortQuery: ['-created_at'],
            stores: null,

            importTypeOptions: [
                {
                    label: this.$t('general.actual'),
                    value: 'actual',
                },
                {
                    label: this.$t('general.budget'),
                    value: 'budget',
                }
            ],

            form: {
                showModal: false,
                type: null,
                file: null,
                isLoading: false,
                importId: null,
                changelist: null,
            },

            confirm: {
                showModal: false,
                import: null,
                dataType: null,
                collection: null,
            },
        };
    },

    metaInfo() {
        return {
            title: this.$t('general.imports'),
        };
    },

    async created() {
        const urlParams = this.$helper.url().parameters();
        await this.loadPage(parseInt(urlParams.get('page[number]') ?? '1'));

        if (urlParams.has('page')) {
            this.page = urlParams.get('page');
        }

        if (urlParams.has('sort')) {
            this.sortQuery = urlParams.get('sort').split(',');
        }
    },

    methods: {
        async loadPage(page) {
            this.page = page;
            await this.applyQuery();
        },

        async applyQuery() {
            this.isLoading = true;
            this.imports = await Import.pageNumber(this.page).orderBy(this.sortQuery).all();
            this.isLoading = false;
        },

        previewImport() {
            this.form.isLoading = true;

            const formData = new FormData();
            formData.append('type', this.form.type.value);
            formData.append('file', this.form.file);

            axios.post(route('api.imports.store'), formData).then(async response => {
                this.form.changelist = response.data.changelist;
                this.form.isLoading = false;

                this.resetForm();
                await this.applyQuery();
                await this.confirmImport(
                    this.imports.data.find(importEntity => {
                        console.log(importEntity);
                        return importEntity.id === response.data.importId;
                    }));
            }).catch(error => {
                console.log(error);
            });
        },

        startImport() {
            this.confirm.isLoading = true;

            axios.post(route('api.imports.run', this.confirm.import.id)).then(async response => {
                this.confirm.isLoading = false;

                this.resetConfirm();
                await this.applyQuery();
            }).catch(error => {
                console.log(error);
                this.resetConfirm();
            });
        },

        resetForm() {
            this.form = {
                showModal: false,
                file: null,
                isSubmitting: false,
                importId: null,
                original: null,
                preview: null,
            };
        },

        resetConfirm() {
            this.confirm = {
                showModal: false,
                import: null,
            };
        },

        async confirmImport(importEntity) {
            this.isFetchingChangelist.push(importEntity.id);
            let collection = new DataColumnCollection();
            let columns = [];
            let changelist = (await axios.get(route('api.imports.changelist', importEntity.id))).data;

            if (!this.stores) {
                let storesResponse;
                let allStores = [];
                let currentPage = 1;
                let totalPages = 1;

                do {
                    storesResponse = await Store.pageNumber(currentPage).all();
                    allStores.push(...storesResponse.data);
                    currentPage++;
                    totalPages = storesResponse.meta.pagination.total_pages;
                } while (
                    storesResponse.meta.pagination
                    && currentPage <= totalPages
                );

                this.stores = allStores;
            }

            changelist.forEach(entity => {
                let column = new DataColumn(new Actual({id: true, ...entity}), true);
                const store = entity.store || this.stores.find(store => store.id === entity.store_id);

                column.header = `
                    <span class="grid gap-2">
                        <span>${store.name}</span>
                        <span>${i18n.t('general.actual')}</span>
                        <span>${moment(entity.date).format('MMMM YYYY')}</span>
                    </span>
                `;

                columns.push(column);
            });

            collection.columns = columns;

            this.confirm = {
                showModal: true,
                import: importEntity,
                dataType: storeManager.rows,
                collection: collection,
            };
            this.isFetchingChangelist.splice(
                this.isFetchingChangelist.findIndex(value => value === importEntity.id),
                1,
            );
        },

        getBadgeStylesFromStatus(importStatus) {
            let styles = [];

            switch (importStatus) {
                case 'imported':
                    styles = ['bg-green-600'];
                    break;
                case 'error':
                    styles = ['bg-red-600'];
                    break;
                case 'preview':
                    styles = ['bg-orange-600'];
                    break;
            }

            return styles;
        },
    },

    watch: {
        sortQuery() {
            this.applyQuery();
        },
    },
};
</script>
